import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { motion } from 'framer-motion';
import { CheckCircle2, Loader2, AlertCircle, ArrowLeft } from 'lucide-react';
import { useEnhancedToast } from "@/components/ui/enhanced-toast";

interface SessionData {
    tenantId: string;
    tenantName: string;
    customCallbackUrl: string;
}

export default function WhatsAppOnboardingCallback() {
    const [isProcessing, setIsProcessing] = useState(true);
    const [isSuccess, setIsSuccess] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [sessionData, setSessionData] = useState<SessionData | null>(null);
    const [iframeUrl, setIframeUrl] = useState<string | null>(null);
    
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const { success: showSuccessToast, error: showErrorToast } = useEnhancedToast();

    useEffect(() => {
        // Listen for messages from iframe
        const handleIframeMessage = (event: MessageEvent) => {
            // Verify origin for security (white-label: don't expose HealVi)
            if (event.origin !== 'https://bridge.healvi-chat.de') {
                return;
            }

            if (event.data.type === 'HEALVI_ONBOARDING_COMPLETE') {
                // Onboarding iframe has completed the setup
                console.log('WhatsApp onboarding completed:', event.data);
                
                // Check if we're in a popup - if so, notify parent and close
                const isPopup = window.opener && window.opener !== window;
                
                if (isPopup) {
                    try {
                        // Send success message to parent window
                        window.opener.postMessage({
                            type: 'WHATSAPP_ONBOARDING_SUCCESS',
                            data: event.data
                        }, window.location.origin);
                        
                        // Clean up session storage
                        sessionStorage.removeItem('whatsapp-onboarding-session');
                        
                        // Close the popup
                        window.close();
                        return;
                    } catch (popupError) {
                        console.error('Failed to communicate with parent window:', popupError);
                        // Fall through to regular handling
                    }
                }
                
                // Regular handling (not in popup)
                setIsSuccess(true);
                setIframeUrl(null); // Hide iframe
                
                showSuccessToast({
                    icon: CheckCircle2,
                    title: "WhatsApp Setup Complete",
                    description: `WhatsApp has been successfully configured for ${sessionData?.tenantName}`,
                    duration: 5000,
                });

                // Clean up session storage
                sessionStorage.removeItem('whatsapp-onboarding-session');
            } else if (event.data.type === 'HEALVI_ONBOARDING_ERROR') {
                // Onboarding iframe encountered an error
                console.error('WhatsApp onboarding error:', event.data.error);
                
                // Check if we're in a popup - if so, notify parent and close
                const isPopup = window.opener && window.opener !== window;
                
                if (isPopup) {
                    try {
                        // Send error message to parent window
                        window.opener.postMessage({
                            type: 'WHATSAPP_ONBOARDING_ERROR',
                            error: event.data.error || 'Setup failed in HealVi'
                        }, window.location.origin);
                        
                        // Close the popup
                        window.close();
                        return;
                    } catch (popupError) {
                        console.error('Failed to communicate error to parent window:', popupError);
                        // Fall through to regular handling
                    }
                }
                
                // Regular handling (not in popup)
                setError(event.data.error || 'WhatsApp setup failed');
                setIframeUrl(null); // Hide iframe
                
                showErrorToast({
                    icon: AlertCircle,
                    title: "Setup Failed",
                    description: event.data.error || 'WhatsApp setup failed',
                    duration: 5000,
                });
            }
        };

        window.addEventListener('message', handleIframeMessage);

        const processCallback = async () => {
            try {
                // Get callback parameters
                const client = searchParams.get('client');
                const channels = searchParams.get('channels');

                if (!client || !channels) {
                    throw new Error('Missing required callback parameters');
                }

                // Note: Don't close popup immediately! We need to show the iframe first
                // The popup will close after the iframe completes the phone selection process

                // Regular handling (not in popup or popup communication failed)
                // Retrieve session data
                const storedData = sessionStorage.getItem('whatsapp-onboarding-session');
                if (!storedData) {
                    throw new Error('Session data not found. Please restart the onboarding process.');
                }

                const parsedSessionData: SessionData = JSON.parse(storedData);
                setSessionData(parsedSessionData);

                // Construct iframe URL with callback parameters using the custom callback URL from API
                const onboardingIframeUrl = `${parsedSessionData.customCallbackUrl}?client=${client}&channels=${encodeURIComponent(channels)}`;
                setIframeUrl(onboardingIframeUrl);

                // Set processing to false to show the iframe
                setIsProcessing(false);

                console.log('WhatsApp onboarding interface ready:', {
                    client,
                    channels,
                    tenantId: parsedSessionData.tenantId
                });

                // Add a fallback timeout (5 minutes) in case setup doesn't complete
                setTimeout(() => {
                    // If still showing iframe (not completed), offer manual completion option
                    if (!isSuccess && !error && iframeUrl) {
                        console.warn('WhatsApp setup timeout - offering manual completion');
                        
                        // Check if we're in a popup
                        const isPopup = window.opener && window.opener !== window;
                        
                        if (isPopup) {
                            try {
                                // Send timeout success to parent window
                                window.opener.postMessage({
                                    type: 'WHATSAPP_ONBOARDING_SUCCESS',
                                    data: { timeout: true }
                                }, window.location.origin);
                                
                                sessionStorage.removeItem('whatsapp-onboarding-session');
                                window.close();
                                return;
                            } catch (popupError) {
                                console.error('Failed to communicate timeout to parent window:', popupError);
                            }
                        }
                        
                        // Regular handling (not in popup)
                        setIsSuccess(true);
                        setIframeUrl(null);
                        
                        showSuccessToast({
                            icon: CheckCircle2,
                            title: "WhatsApp Setup Complete",
                            description: `WhatsApp has been configured for ${parsedSessionData.tenantName}. Please refresh the messaging page to see the connection status.`,
                            duration: 7000,
                        });

                        sessionStorage.removeItem('whatsapp-onboarding-session');
                    }
                }, 300000); // 5 minutes

            } catch (err) {
                console.error('Onboarding callback error:', err);
                const errorMessage = err instanceof Error ? err.message : 'An unexpected error occurred';
                
                // Check if we're in a popup window for error handling too
                const isPopup = window.opener && window.opener !== window;
                
                if (isPopup) {
                    try {
                        // Send error message to parent window
                        window.opener.postMessage({
                            type: 'WHATSAPP_ONBOARDING_ERROR',
                            error: errorMessage
                        }, window.location.origin);
                        
                        // Close the popup
                        window.close();
                        return;
                    } catch (popupError) {
                        console.error('Failed to communicate error to parent window:', popupError);
                        // Fall through to regular error handling
                    }
                }
                
                // Regular error handling
                setError(errorMessage);
                setIsProcessing(false);
                
                showErrorToast({
                    icon: AlertCircle,
                    title: "Setup Failed",
                    description: errorMessage,
                    duration: 5000,
                });
            }
        };

        processCallback();

        // Cleanup function
        return () => {
            window.removeEventListener('message', handleIframeMessage);
        };
    }, [searchParams, showSuccessToast, showErrorToast, sessionData?.tenantName]); // eslint-disable-line react-hooks/exhaustive-deps

    const handleReturnToMessaging = () => {
        navigate('/dashboard/messaging');
    };

    return (
        <div className="min-h-screen bg-white flex items-center justify-center p-4">
            <div className="w-full max-w-2xl">
                {/* Processing State */}
                {isProcessing && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center"
                    >
                        <Loader2 className="w-8 h-8 text-blue-500 animate-spin mx-auto mb-4" />
                        <p className="text-gray-600">Loading setup...</p>
                    </motion.div>
                )}

                {/* Setup Interface */}
                {iframeUrl && !isSuccess && !error && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.3 }}
                        className="border border-gray-200 rounded-lg overflow-hidden shadow-sm bg-white"
                    >
                        <iframe
                            src={iframeUrl}
                            className="w-full h-[600px]"
                            frameBorder="0"
                            title="WhatsApp Business Setup"
                            allow="clipboard-read; clipboard-write"
                            sandbox="allow-scripts allow-same-origin allow-forms allow-popups"
                        />
                    </motion.div>
                )}

                {/* Success State */}
                {isSuccess && sessionData && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center space-y-4"
                    >
                        <CheckCircle2 className="w-12 h-12 text-green-500 mx-auto" />
                        <h2 className="text-xl font-semibold text-green-600">Setup Complete!</h2>
                        <p className="text-gray-600">
                            WhatsApp Business configured for {sessionData.tenantName}
                        </p>
                        <Button onClick={handleReturnToMessaging} className="bg-green-600 hover:bg-green-700">
                            <ArrowLeft className="w-4 h-4 mr-2" />
                            Return to Messaging
                        </Button>
                    </motion.div>
                )}

                {/* Error State */}
                {error && (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        className="text-center space-y-4"
                    >
                        <AlertCircle className="w-12 h-12 text-red-500 mx-auto" />
                        <h2 className="text-xl font-semibold text-red-600">Setup Failed</h2>
                        <p className="text-gray-600">{error}</p>
                        <div className="flex gap-3 justify-center">
                            <Button variant="outline" onClick={() => window.location.reload()}>
                                Try Again
                            </Button>
                            <Button onClick={handleReturnToMessaging} variant="outline">
                                <ArrowLeft className="w-4 h-4 mr-2" />
                                Return
                            </Button>
                        </div>
                    </motion.div>
                )}
            </div>
        </div>
    );
}